import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { notify } from "../../../components-ui/toast";
import {
  PartyRelationshipContactPreference,
  PartyRelationshipSummary,
} from "../../../types/types";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import { useStaticData } from "../../../../context/StaticDataContext";
import { RELATIONSHIP_CATEGORIES } from "../../../../constants/constants";
import { api } from "../../../../api/apiClient";
import NewOrganisationMemberForm from "./NewOrganisationMemberForm";

interface OrganisationMembersCardProps {
  partyId: number;
  partyRelationships: PartyRelationshipSummary[];
  onChange: (
    partyRelationships: PartyRelationshipSummary[],
    persisted?: boolean,
  ) => void;
  relationshipApiBase?: string;
  serviceOptionsUrl?: string;
  onHeaderActionChange?: (action: ReactNode | null) => void;
}

const OrganisationMembersCard: React.FC<OrganisationMembersCardProps> = ({
  partyId,
  partyRelationships,
  onChange,
  relationshipApiBase,
  serviceOptionsUrl,
  onHeaderActionChange,
}) => {
  const { data } = useStaticData();
  const relationshipTypes =
    data?.relationshipTypes?.[RELATIONSHIP_CATEGORIES.ORGANISATION] || [];

  const [showForm, setShowForm] = useState(false);
  const [editMember, setEditMember] = useState<PartyRelationshipSummary | null>(
    null,
  );
  const [showNewPartyForm, setShowNewPartyForm] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");

  const members = useMemo(
    () =>
      partyRelationships.filter(
        (relationship) => relationship.toPartyId === partyId,
      ),
    [partyRelationships, partyId],
  );

  const handleDelete = async (partyRelationshipId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this member from the organisation?",
      )
    )
      return;
    try {
      if (relationshipApiBase && partyRelationshipId > 0) {
        await api<void>(`${relationshipApiBase}/${partyRelationshipId}`, {
          method: "DELETE",
        });
      }
      onChange(
        partyRelationships.filter(
          (relationship) =>
            relationship.partyRelationshipId !== partyRelationshipId,
        ),
        Boolean(relationshipApiBase),
      );
      notify.success(
        relationshipApiBase
          ? "Organisation member removed"
          : "Organisation member removed from the form",
      );
    } catch (error) {
      console.error("Failed to remove organisation member", error);
      notify.error("Failed to remove organisation member");
    }
  };

  const handleEdit = (member: PartyRelationshipSummary) => {
    setShowNewPartyForm(false);
    setEditMember(member);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setShowNewPartyForm(false);
    setEditMember(null);
  };

  const isEditing = showForm || showNewPartyForm;
  const addMemberButton = useMemo(
    () => (
      <button
        type="button"
        className={`icon-button-solid ${
          isEditing
            ? "icon-button-solid--danger"
            : "icon-button-solid--success"
        }`}
        onClick={() => {
          if (isEditing) {
            setShowForm(false);
            setShowNewPartyForm(false);
            setEditMember(null);
          } else {
            setShowNewPartyForm(true);
          }
        }}
        title={isEditing ? "Cancel" : "Add member"}
        aria-label={isEditing ? "Cancel" : "Add member"}
      >
        {isEditing ? (
          <CloseIcon fontSize="small" />
        ) : (
          <AddIcon fontSize="small" />
        )}
      </button>
    ),
    [isEditing],
  );
  useEffect(() => {
    if (!onHeaderActionChange) return;

    onHeaderActionChange(addMemberButton);
    return () => onHeaderActionChange(null);
  }, [addMemberButton, onHeaderActionChange]);

  const handleMembershipSaved = async (
    savedRelationship: PartyRelationshipSummary,
    addAnother = false,
  ) => {
    const nextRelationships = [...partyRelationships];
    let relationshipToStore = savedRelationship;

    try {
      if (relationshipApiBase) {
        relationshipToStore = await api<PartyRelationshipSummary>(
          savedRelationship.partyRelationshipId > 0
            ? `${relationshipApiBase}/${savedRelationship.partyRelationshipId}`
            : relationshipApiBase,
          {
            method: savedRelationship.partyRelationshipId > 0 ? "PUT" : "POST",
            body: savedRelationship,
          },
        );
      } else if (savedRelationship.partyRelationshipId <= 0) {
        relationshipToStore = {
            ...savedRelationship,
            partyRelationshipId:
              Math.min(
                0,
                ...nextRelationships
                  .map((relationship) => relationship.partyRelationshipId)
                  .filter((relationshipId) => relationshipId < 0),
              ) - 1,
          };
      }

    const existingIndex = nextRelationships.findIndex(
      (relationship) =>
        relationship.partyRelationshipId ===
        relationshipToStore.partyRelationshipId,
    );

    if (existingIndex >= 0) {
      nextRelationships[existingIndex] = relationshipToStore;
    } else {
      nextRelationships.push(relationshipToStore);
    }

      onChange(nextRelationships, Boolean(relationshipApiBase));
      if (!addAnother) {
        handleFormClose();
      }
      notify.success(
        relationshipApiBase
          ? "Organisation member saved"
          : savedRelationship.partyRelationshipId > 0
            ? "Organisation member updated in the form"
            : "Organisation member added to the form",
      );
    } catch (error) {
      console.error("Failed to save organisation member", error);
      notify.error("Failed to save organisation member");
      throw error;
    }
  };

  const getRelationshipTypeName = (relationshipTypeId: number) => {
    return (
      relationshipTypes.find(
        (relationshipType) =>
          relationshipType.relationshipTypeId === relationshipTypeId,
      )?.name ?? String(relationshipTypeId)
    );
  };

  const getContactPreferenceLabel = (
    preference: PartyRelationshipContactPreference,
  ) => {
    return (
      data?.contactTypes.find(
        (contactType) => contactType.contactTypeId === preference.contactTypeId,
      )?.name ?? preference.contactTypeCode
    );
  };

  const getContactPreferenceSummary = (member: PartyRelationshipSummary) => {
    if (!member.contactPreferences?.length) {
      return "Uses party defaults";
    }

    const enabledChannels = member.contactPreferences
      .filter((preference) => preference.isOptedIn)
      .map(getContactPreferenceLabel);

    return enabledChannels.length > 0
      ? enabledChannels.join(", ")
      : "No channels enabled";
  };

  const filteredMembers = useMemo(() => {
    const term = memberSearch.trim().toLowerCase();
    if (!term) return members;

    return members.filter((member) =>
      `${member.fromPartyName} ${getRelationshipTypeName(member.relationshipTypeId)} ${getContactPreferenceSummary(member)}`
        .toLowerCase()
        .includes(term),
    );
  }, [memberSearch, members, relationshipTypes, data?.contactTypes]);

  return (
    <div className={onHeaderActionChange ? "organisation-members-management" : "card"}>
      {!onHeaderActionChange && (
        <div className="card-header-row">
          <div className="card-header">
            <div className="card-title">Organisation Members</div>
          </div>
          {addMemberButton}
        </div>
      )}

      {showNewPartyForm && (
        <NewOrganisationMemberForm
          organisationId={partyId}
          serviceOptionsUrl={serviceOptionsUrl}
          onCancel={handleFormClose}
          onSaved={handleMembershipSaved}
        />
      )}
      {showForm && editMember && (
        <NewOrganisationMemberForm
          organisationId={partyId}
          initialRelationship={editMember}
          serviceOptionsUrl={serviceOptionsUrl}
          relationshipUrl={relationshipApiBase ? `${relationshipApiBase}/${editMember.partyRelationshipId}` : undefined}
          onCancel={handleFormClose}
          onSaved={handleMembershipSaved}
        />
      )}

      {!isEditing && (
        <>
          <div className="organisation-members-toolbar">
            <div className="search-container organisation-members-search">
              <div className="search-input-wrapper">
                <SearchIcon className="search-icon-left" fontSize="small" />
                <input
                  className="form-input search-input-with-icons"
                  placeholder="Search members..."
                  value={memberSearch}
                  onChange={(event) => setMemberSearch(event.target.value)}
                />
                {memberSearch && (
                  <button
                    type="button"
                    className="search-clear-button"
                    onClick={() => setMemberSearch("")}
                    aria-label="Clear member search"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {filteredMembers.length === 0 ? (
            <p className="organisation-members-empty">No members found.</p>
          ) : (
            <div className="organisation-members-list" role="list">
              {filteredMembers.map((member) => (
                <div
                  key={member.partyRelationshipId}
                  className="organisation-member-row"
                  role="listitem"
                >
                  <div className="organisation-member-main">
                    <span className="organisation-member-icon" aria-hidden="true">
                      <PersonIcon />
                    </span>
                    <div className="organisation-member-copy">
                      <div className="organisation-member-name">
                        {member.fromPartyName}
                      </div>

                      <div className="organisation-member-meta">
                        <span className="organisation-member-role">
                          {getRelationshipTypeName(member.relationshipTypeId)}
                        </span>
                        <span className="organisation-member-preferences">
                          {getContactPreferenceSummary(member)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="organisation-member-actions">
                    <button
                      className="icon-button"
                      title="Edit"
                      type="button"
                      onClick={() => handleEdit(member)}
                    >
                      <EditIcon fontSize="small" />
                    </button>
                    <button
                      className="icon-button"
                      title="Delete"
                      type="button"
                      onClick={() => handleDelete(member.partyRelationshipId)}
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrganisationMembersCard;
